// Cross-tab sign-in code hand-off (Duncan GO 2026-10-22; governing: the
// email-code sign-in — the email link NEVER signs in on its own).
//
// The sign-in email's button opens a NEW my.trumin tab at /sign-in?code=…
// while the person's ORIGINAL tab is still waiting on "Check your email".
// The new tab OFFERS the code to the waiting tab (same origin only:
// BroadcastChannel, with a localStorage `storage`-event fallback); the waiting
// tab fills its code field and ACKs, and the person presses Sign in THERE.
// No ack within ~1 s ⇒ the new tab keeps today's behaviour (form with the
// code filled in). The code is never logged, never kept in localStorage after
// the offer, and never left in the URL.

const CHANNEL = 'mytrumin-signin-code'
const OFFER_KEY = 'mytrumin.signin.handoff'
const ACK_KEY = 'mytrumin.signin.handoff.ack'

type Msg = { t: 'code'; id: string; code: string } | { t: 'ack'; id: string }

function channel(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL) : null
  } catch {
    return null
  }
}

function newId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

/** WAITING tab: call onCode for a code another tab offers; ACK it. Returns a
 *  cleanup. Only codes of a plausible shape are accepted. */
export function listenForHandedOffCode(onCode: (code: string) => void): () => void {
  const ch = channel()
  const accept = (id: unknown, code: unknown) => {
    if (typeof id !== 'string' || typeof code !== 'string' || !/^[0-9A-Za-z]{4,12}$/.test(code)) return
    onCode(code)
    try {
      ch?.postMessage({ t: 'ack', id } satisfies Msg)
    } catch {
      /* channel closed */
    }
    try {
      localStorage.setItem(ACK_KEY, id)
      localStorage.removeItem(ACK_KEY)
    } catch {
      /* storage unavailable */
    }
  }
  const onMessage = (e: MessageEvent) => {
    const m = e.data as Partial<Msg> | null
    if (m && m.t === 'code') accept(m.id, (m as { code?: unknown }).code)
  }
  const onStorage = (e: StorageEvent) => {
    if (e.key !== OFFER_KEY || !e.newValue) return
    try {
      const v = JSON.parse(e.newValue) as { id?: unknown; code?: unknown }
      accept(v.id, v.code)
    } catch {
      /* malformed */
    }
  }
  ch?.addEventListener('message', onMessage)
  window.addEventListener('storage', onStorage)
  return () => {
    ch?.removeEventListener('message', onMessage)
    ch?.close()
    window.removeEventListener('storage', onStorage)
  }
}

/** NEW (email-link) tab: offer the code to a waiting tab; resolves true when
 *  one ACKs within `timeoutMs`, else false. Leaves nothing in storage. */
export function offerCodeToWaitingTab(code: string, timeoutMs = 1000): Promise<boolean> {
  const id = newId()
  const ch = channel()
  return new Promise<boolean>((resolve) => {
    let done = false
    const finish = (ok: boolean) => {
      if (done) return
      done = true
      clearTimeout(timer)
      ch?.removeEventListener('message', onMessage)
      ch?.close()
      window.removeEventListener('storage', onStorage)
      try {
        localStorage.removeItem(OFFER_KEY)
      } catch {
        /* storage unavailable */
      }
      resolve(ok)
    }
    const onMessage = (e: MessageEvent) => {
      const m = e.data as Partial<Msg> | null
      if (m && m.t === 'ack' && m.id === id) finish(true)
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACK_KEY && e.newValue === id) finish(true)
    }
    const timer = setTimeout(() => finish(false), timeoutMs)
    ch?.addEventListener('message', onMessage)
    window.addEventListener('storage', onStorage)
    try {
      ch?.postMessage({ t: 'code', id, code } satisfies Msg)
    } catch {
      /* channel closed */
    }
    try {
      localStorage.setItem(OFFER_KEY, JSON.stringify({ id, code }))
    } catch {
      /* storage unavailable */
    }
  })
}

/** Drop ?code / ?u from the address bar (the code never stays in the URL). */
export function stripCodeFromUrl(): void {
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('code') && !url.searchParams.has('u')) return
    url.searchParams.delete('code')
    url.searchParams.delete('u')
    const q = url.searchParams.toString()
    window.history.replaceState(window.history.state, '', `${url.pathname}${q ? `?${q}` : ''}${url.hash}`)
  } catch {
    /* non-strippable URL */
  }
}
